import React from 'react';
import eventsApi from '../../../apiFetcher/eventsApiFetcher';
import { isDateInPast } from '../util';
import { initialState as eventInitialState } from './EventDataStoreContext/EventDataStoreContext';
import {
	Event,
	EventRequest,
	EventRequestRef,
} from './EventDataStoreContext/types';
import { MyEventsFilteringEnum } from '../enums/MyEventsFilteringEnum';
import { useUser } from '../../User/containers/UserDataStore';
import { useAccountOrMembershipRequiredModalContext } from '../../Anonymous/AccountOrMembershipRequiredModal/contexts/AccountOrMembershipRequiredModalContext';

const initialStoreValue = {
	isLoading: false,
	upcomingEvents: [],
	pastEvents: [],
	refresh: () => {},
	refreshWithoutLoadingState: () => {},
	filterEventsByCommunityIds: ([], []) => [],
	buildEvent: () => eventInitialState,
	getMyEvents: () => [],
	sortEventsInAscendingOrderByStartTime: ([]) => [],
};

interface IStoreValue {
	isLoading: boolean;
	upcomingEvents: Event[];
	pastEvents: Event[];
	refresh: () => void;
	refreshWithoutLoadingState: () => void;
	filterEventsByCommunityIds: (
		events: Event[],
		communityIds: number[],
	) => Event[];
	buildEvent: (event: Event) => Event;
	getMyEvents: (filterType: MyEventsFilteringEnum) => Event[];
	sortEventsInAscendingOrderByStartTime: (eventsToSort: Event[]) => Event[];
}

const EventsContext = React.createContext<IStoreValue>(initialStoreValue);

export const useEventsDataStore = () => React.useContext(EventsContext);

const eventsRequestInitialValue: EventRequestRef = {
	cancel: () => {},
	promise: new Promise<Event[]>(resolve => {
		resolve([]);
	}),
};

export const EventsDataStoreProvider = ({ children }) => {
	const [isLoading, setIsLoading] = React.useState(false);

	const { isAnonymous } = useUser();
	const { isNonMember } = useAccountOrMembershipRequiredModalContext();

	const showAnonOrNonmemberView = isAnonymous() || isNonMember();

	const [
		eventsWithJoinedCommunities,
		setEventsWithJoinedCommunities,
	] = React.useState<Event[]>([]);
	const [
		eventsWithGenreCommunities,
		setEventsWithGenreCommunities,
	] = React.useState<Event[]>([]);
	const [ownedEvents, setOwnedEvents] = React.useState<Event[]>([]);

	const events = showAnonOrNonmemberView
		? eventsWithGenreCommunities
		: eventsWithJoinedCommunities;
	const upcomingEvents: Event[] = events.filter(
		(e: Event) => !isDateInPast(e.time.endTime),
	);
	const pastEvents: Event[] = events
		.filter((e: Event) => isDateInPast(e.time.endTime))
		.reverse();

	const eventsWithJoinedCommunitiesRequest: EventRequest = {
		ref: React.useRef(eventsRequestInitialValue),
		route: '/api/events/communities/joined',
		setEvents: setEventsWithJoinedCommunities,
	};

	const eventsWithGenreCommunitiesRequest: EventRequest = {
		ref: React.useRef(eventsRequestInitialValue),
		route: '/api/events/communities/genre',
		setEvents: setEventsWithGenreCommunities,
	};

	const ownedEventsRequest: EventRequest = {
		ref: React.useRef(eventsRequestInitialValue),
		route: '/api/events/owned',
		setEvents: setOwnedEvents,
	};

	const anonymousRequests: EventRequest[] = [
		eventsWithGenreCommunitiesRequest,
	];
	const authenticatedRequests: EventRequest[] = [
		eventsWithJoinedCommunitiesRequest,
		ownedEventsRequest,
	];
	const requestsToMake: EventRequest[] = showAnonOrNonmemberView
		? anonymousRequests
		: authenticatedRequests;

	React.useEffect(() => {
		fetchEvents();

		return () => {
			requestsToMake.forEach((request: EventRequest) => {
				request.ref.current.cancel();
			});
		};
	}, [showAnonOrNonmemberView]);

	const fetchEvents = async (withLoadingState = true) => {
		withLoadingState && setIsLoading(true);
		try {
			requestsToMake.forEach((request: EventRequest) => {
				request.ref.current = eventsApi.cancelableGet(request.route);
			});

			const responses: PromiseSettledResult<
				Event[]
			>[] = await Promise.allSettled(
				requestsToMake.map(request => request.ref.current.promise),
			);

			requestsToMake.forEach((request: EventRequest, index: number) => {
				handleEventsResponse(responses[index], request.setEvents);
			});
		} catch (e) {
			console.error(e);
		} finally {
			withLoadingState && setIsLoading(false);
		}
	};

	const handleEventsResponse = (
		response: PromiseSettledResult<Event[]>,
		setEvents: React.Dispatch<React.SetStateAction<Event[]>>,
	) => {
		if (response.status === 'fulfilled') {
			const fulfilledResponse: PromiseFulfilledResult<Event[]> = response;
			const events: Event[] = fulfilledResponse.value || [];
			const formattedAndSortedEvents = formatAndSortEvents(events);
			setEvents(formattedAndSortedEvents);
		} else {
			console.error(response.reason);
		}
	};

	const buildEvent = (event: Event) => ({
		...event,
		venue: event.venue || {},
		links: event.links || {},
		time: event.time || {},
		organization: event.organization || {},
		images: event.images || {},
	});

	const formatAndSortEvents = (eventsToSave: Event[]) => {
		const formattedEvents = eventsToSave.map(buildEvent);
		const formattedAndSortedEvents: Event[] = sortEventsInAscendingOrderByStartTime(
			formattedEvents,
		);
		return formattedAndSortedEvents;
	};

	const sortEventsInAscendingOrderByStartTime = (
		eventsToSort: Event[],
	): Event[] =>
		Array.isArray(eventsToSort)
			? eventsToSort.sort(
					(a: Event, b: Event) =>
						+new Date(a.time.startTime) -
						+new Date(b.time.startTime),
			  )
			: [];

	const filterEventsByCommunityIds = (
		events: Event[],
		communityIds: number[],
	) => {
		return events.filter((event: Event) =>
			event.communities.some(comm =>
				communityIds.includes(Number(comm.communityId)),
			),
		);
	};

	const getMyEvents = (filterType: MyEventsFilteringEnum) => {
		switch (filterType) {
			case MyEventsFilteringEnum.Upcoming:
				return ownedEvents.filter(e => !isDateInPast(e.time.endTime));
			case MyEventsFilteringEnum.Past:
				return ownedEvents
					.filter(e => isDateInPast(e.time.endTime))
					.reverse();
			default:
				return ownedEvents;
		}
	};

	const getStore = () => ({
		isLoading,
		upcomingEvents,
		pastEvents,
		refresh: () => fetchEvents(),
		refreshWithoutLoadingState: () => fetchEvents(false),
		filterEventsByCommunityIds,
		buildEvent,
		getMyEvents,
		sortEventsInAscendingOrderByStartTime,
	});

	return (
		<EventsContext.Provider value={getStore()}>
			{typeof children === 'function' ? children(getStore()) : children}
		</EventsContext.Provider>
	);
};
